import NoneChip from '@/components/chip/none-chip';
import StatusChip from '@/components/chip/status-chip';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import MarkdownContent from '@/components/markdown-content';
import InlineToast from '@/components/toasts/inline';
import { pluralize } from '@/lib/helpers';
import { ClassifierModel, ClassifierSubType } from '@/lib/models/classifier';
import { patternUsesTextAnchors, REGEX_ANCHOR_WARNING } from '@/lib/regex-helpers';
import { Stack } from '@mui/material';
import { FC } from 'react';

interface Props {
  classifier: ClassifierModel;
}

const getFields = (classifier: ClassifierModel): ReadonlyField[] => {
  const { subtype } = classifier;
  const baseFields: ReadonlyField[] = [
    { label: 'Label', prop: 'name' },
    { label: 'Description', prop: 'description' },
    { label: 'Status', prop: 'status' },
    { label: 'Type', prop: 'type' },
  ];

  if (subtype === ClassifierSubType.Regex) {
    baseFields.push({ label: 'Regex Pattern', prop: 'regexPattern' });
  } else if (subtype === ClassifierSubType.Custom) {
    baseFields.push({ label: 'Training Examples', prop: 'messages' });
  }
  return baseFields;
};

const getReadonlyData = (classifier: ClassifierModel) => {
  const { name, description, enabled, subtype, phrases, config } = classifier;

  const isRegex = subtype === ClassifierSubType.Regex;
  const regexPattern = isRegex ? config?.pattern.trim() : undefined;
  const hasDescription = !!description;
  const enabledStatus = enabled ? 'enabled' : 'disabled';
  const phraseCount = phrases?.length || 0;
  const hasPhrases = !!phraseCount;
  const phraseCountLabel = `${phraseCount} ${pluralize(phraseCount, 'message')}`;
  const hasRegexPattern = !!regexPattern?.trim();
  const showAnchorWarning = hasRegexPattern && patternUsesTextAnchors(regexPattern as string);

  const messages = hasPhrases ? (
    phraseCountLabel
  ) : (
    <Stack gap={2}>
      <NoneChip />
      <InlineToast
        message="Without example messages, this intent topic cannot be classified."
        level="info"
      />
    </Stack>
  );

  const regexPatternDisplay = hasRegexPattern ? (
    <>
      <MarkdownContent message={`\`\`\`regex\n${regexPattern}\n\`\`\``} />{' '}
      {showAnchorWarning && <InlineToast level="warning" message={REGEX_ANCHOR_WARNING} />}
    </>
  ) : (
    <Stack gap={2}>
      <NoneChip />
      <InlineToast
        message="Without a regex pattern, this custom entity cannot match any text."
        level="info"
      />
    </Stack>
  );

  return {
    name,
    description: hasDescription ? description : <NoneChip />,
    status: <StatusChip value={enabledStatus} />,
    type: isRegex ? 'Entity' : 'Intent',
    messages,
    regexPattern: regexPatternDisplay,
  };
};

const ClassifierDetails: FC<Props> = ({ classifier }) => {
  const fields = getFields(classifier);
  const data = getReadonlyData(classifier);

  return <ReadonlyView fields={fields} data={data} />;
};

export default ClassifierDetails;
